import type {
  BrowserEventPayload,
  BrowserPixelAdapter,
  ScriptErrorReporter,
} from "../BrowserPixelAdapter";
import { injectVendorScript } from "../BrowserPixelAdapter";
import {
  COOKIEBOT_CONSENT_EVENTS,
  readCookiebotMarketingConsent,
  readPageAdConsent,
} from "../PageConsent";
import type { AdConsentState } from "../PageConsent";

export {
  TaboolaPixelAdapter,
  TABOOLA_ADAPTER_KEY,
  TABOOLA_SCRIPT_ID,
  TABOOLA_CONSENT_WAIT_MS,
  TABOOLA_CONSENT_WATCH_MS,
  taboolaScriptUrl,
};

const TABOOLA_ADAPTER_KEY = "taboola";
// El id del <script> del snippet oficial: si ya está en la página (el tenant o
// GTM instalaron el píxel), no se inyecta otro
const TABOOLA_SCRIPT_ID = "tb_tfa_script";
const TABOOLA_PAGE_VIEW = "page_view";
// Lo que se espera, como mucho, a conocer el consentimiento de la página antes
// de mandar nada (igual que con la UET)
const TABOOLA_CONSENT_WAIT_MS = 1500;
const TABOOLA_CONSENT_POLL_MS = 100;
// Con la página denegada, cada cuánto y hasta cuándo se vuelve a mirar el
// Consent Mode: un banner que no es Cookiebot solo avisa a través de él
const TABOOLA_CONSENT_WATCH_POLL_MS = 1000;
const TABOOLA_CONSENT_WATCH_MS = 120000;

type TfaCommand = Record<string, unknown>;

function taboolaScriptUrl(accountId: string): string {
  return (
    "https://cdn.taboola.com/libtrc/unip/" +
    encodeURIComponent(accountId) +
    "/tfa.js"
  );
}

/**
 * Adaptador del Taboola Pixel (`tfa.js`, cola global `_tfa`).
 *
 * Lo que viaja hecho en el manifest: el `pixel_id` es el id numérico de la
 * cuenta de Taboola y cada evento lleva como nombre el `event_name` de su
 * regla de conversión, con `{revenue, currency}` si hay valor.
 * Particularidades de la red que se resuelven aquí:
 *
 * - **Cola compartida**: `_tfa` es una sola para todas las cuentas de la
 *   página (cada comando lleva su `id`), como en el snippet oficial: si ya
 *   existe (el tenant o GTM instalaron el píxel) se reutiliza, y `tfa.js` solo
 *   se inyecta si no hay ya un `#tb_tfa_script`.
 * - **Página vista automática**: al instalar cada cuenta se manda
 *   `{notify: 'event', name: 'page_view', id}`, como el snippet. Es lo que
 *   alimenta las audiencias de retargeting y las conversiones por URL; no hay
 *   evento de página vista en el manifest de Taboola.
 * - **Conversión con `orderid`**: el `event_id` común viaja como `orderid`, el
 *   mismo que lleva la copia de servidor (S2S). Taboola deduplica las dos del
 *   mismo clic y se queda con la de servidor.
 * - **Sin datos del visitante**: Taboola no casa conversiones por email o
 *   teléfono, así que no se le manda identidad.
 * - **Consentimiento**: Taboola no tiene un modo de consentimiento propio
 *   fuera del TCF, así que el adaptador no le manda nada (ni la página vista)
 *   mientras el consentimiento de anuncios de la página esté denegado: el
 *   Consent Mode de Google o, si no lo hay, Cookiebot. Mientras no se sepa se
 *   espera hasta `TABOOLA_CONSENT_WAIT_MS`; sin ninguno de los dos en la
 *   página, se manda, como el snippet oficial. Lo pendiente sale en cuanto el
 *   visitante acepta en Cookiebot, llega `setConsent(true)` o el Consent Mode
 *   pasa a concedido (se vuelve a mirar con cada evento y cada segundo durante
 *   `TABOOLA_CONSENT_WATCH_MS`: un banner que no es Cookiebot solo avisa así).
 */
class TaboolaPixelAdapter implements BrowserPixelAdapter {
  readonly key = TABOOLA_ADAPTER_KEY;
  private _loaded = false;
  private _onScriptError: ScriptErrorReporter | null;
  private _initialized: Record<string, boolean> = {};
  private _scriptRequested = false;
  // Comandos a la espera del consentimiento
  private _pending: TfaCommand[] = [];
  // null = aún no se sabe si se puede mandar
  private _allowed: boolean | null = null;
  private _waitStartedAt: number | null = null;
  private _watchStartedAt: number | null = null;
  // Lo último que dijo `setConsent` (manda sobre lo que se lea de la página)
  private _explicitConsent: AdConsentState | null = null;

  constructor(onScriptError?: ScriptErrorReporter | null) {
    this._onScriptError = onScriptError ?? null;
  }

  load(): void {
    if (this._loaded) {
      return;
    }
    this._loaded = true;
    try {
      const scope = window as unknown as Record<string, unknown>;
      if (!Array.isArray(scope._tfa) && scope._tfa === undefined) {
        scope._tfa = [];
      }
      for (const name of COOKIEBOT_CONSENT_EVENTS) {
        window.addEventListener(name, () => this._relayCookiebotConsent());
      }
    } catch {
      // sin píxel de Taboola la página sigue funcionando igual
    }
  }

  // Sin identidad: Taboola no casa conversiones por datos del visitante
  init(pixelId: string): void {
    try {
      if (!this._loaded || !pixelId || this._initialized[pixelId]) {
        return;
      }
      this._initialized[pixelId] = true;
      this._send(
        { notify: "event", name: TABOOLA_PAGE_VIEW, id: accountIdOf(pixelId) },
        pixelId,
      );
    } catch {
      // un init fallido no puede tumbar el resto de píxeles
    }
  }

  track(pixelId: string, event: BrowserEventPayload, eventId: string): void {
    try {
      if (!this._initialized[pixelId] || !event?.name) {
        return;
      }
      const command: TfaCommand = {
        ...(event.params ?? {}),
        notify: "event",
        name: event.name,
        id: accountIdOf(pixelId),
      };
      if (eventId) {
        command.orderid = eventId;
      }
      this._send(command, pixelId);
    } catch {
      // el disparo de un píxel jamás afecta al lead ni a la navegación
    }
  }

  setConsent(granted: boolean): void {
    try {
      this._explicitConsent = granted ? "granted" : "denied";
      this._applyConsent(this._explicitConsent);
    } catch {
      // sin consentimiento aplicado, el píxel se queda como estaba
    }
  }

  private _send(command: TfaCommand, pixelId: string): void {
    if (this._allowed === false && this._explicitConsent === null) {
      this._rereadPageConsent();
    }
    if (this._allowed === true) {
      this._flushOne(command, pixelId);
      return;
    }
    if (this._allowed === false && this._explicitConsent === "denied") {
      // Revocado de forma explícita: no se guarda nada para después
      return;
    }
    this._pending.push({ ...command, __pixelId: pixelId });
    if (this._allowed === null) {
      this._decideWhenConsentKnown();
    }
  }

  private _decideWhenConsentKnown(): void {
    if (this._waitStartedAt === null) {
      this._waitStartedAt = Date.now();
    }
    if (this._explicitConsent !== null) {
      this._applyConsent(this._explicitConsent);
      return;
    }
    const consent = readPageAdConsent();
    if (consent !== null) {
      this._applyConsent(consent.current);
      if (!this._allowed) {
        this._watchPageConsent();
      }
      return;
    }
    if (Date.now() - this._waitStartedAt < TABOOLA_CONSENT_WAIT_MS) {
      setTimeout(() => {
        try {
          if (this._allowed === null) {
            this._decideWhenConsentKnown();
          }
        } catch {
          // sin decisión, el píxel no manda nada
        }
      }, TABOOLA_CONSENT_POLL_MS);
      return;
    }
    // La página no tiene banner ni Consent Mode: como el snippet oficial
    this._applyConsent("granted");
  }

  // El Consent Mode pudo pasar a concedido después de leerlo (el banner, o lo que ya aceptó en otra visita)
  private _rereadPageConsent(): void {
    if (readPageAdConsent()?.current === "granted") {
      this._applyConsent("granted");
    }
  }

  private _watchPageConsent(): void {
    if (this._watchStartedAt === null) {
      this._watchStartedAt = Date.now();
    }
    setTimeout(() => {
      try {
        if (this._allowed !== false || this._explicitConsent !== null) {
          return;
        }
        this._rereadPageConsent();
        if (
          this._allowed === false &&
          this._watchStartedAt !== null &&
          Date.now() - this._watchStartedAt < TABOOLA_CONSENT_WATCH_MS
        ) {
          this._watchPageConsent();
        }
      } catch {
        // sin la vigilancia, lo pendiente sale con el próximo evento o con Cookiebot
      }
    }, TABOOLA_CONSENT_WATCH_POLL_MS);
  }

  private _applyConsent(state: AdConsentState): void {
    this._allowed = state === "granted";
    if (!this._allowed) {
      if (this._explicitConsent === "denied") {
        this._pending = [];
      }
      return;
    }
    const pending = this._pending;
    this._pending = [];
    for (const { __pixelId, ...command } of pending) {
      this._flushOne(command, String(__pixelId));
    }
  }

  private _flushOne(command: TfaCommand, pixelId: string): void {
    const scope = window as unknown as Record<string, unknown>;
    if (scope._tfa === undefined) {
      scope._tfa = [];
    }
    const queue = scope._tfa as { push?: (item: unknown) => unknown };
    if (typeof queue?.push !== "function") {
      return;
    }
    queue.push(command);
    this._ensureScript(pixelId);
  }

  // `tfa.js` una vez por página, con la primera cuenta: atiende a todas las de la cola
  private _ensureScript(pixelId: string): void {
    if (this._scriptRequested) {
      return;
    }
    this._scriptRequested = true;
    if (document.getElementById(TABOOLA_SCRIPT_ID)) {
      return;
    }
    injectVendorScript(
      taboolaScriptUrl(pixelId),
      this.key,
      this._onScriptError,
    );
    const scripts = document.getElementsByTagName("script");
    for (let i = 0; i < scripts.length; i++) {
      if (scripts[i].src === taboolaScriptUrl(pixelId)) {
        scripts[i].id = TABOOLA_SCRIPT_ID;
        break;
      }
    }
  }

  private _relayCookiebotConsent(): void {
    try {
      if (this._explicitConsent !== null) {
        return;
      }
      const consent = readCookiebotMarketingConsent();
      if (consent !== null) {
        this._applyConsent(consent);
      }
    } catch {
      // sin el relevo, el píxel se queda con lo que ya sabía
    }
  }
}

// En el snippet oficial el id de la cuenta va como número
function accountIdOf(pixelId: string): number | string {
  return /^\d+$/.test(pixelId) ? Number(pixelId) : pixelId;
}
